import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Building2, Plus, ArrowRightLeft, Loader2, LogOut,
  CheckCircle2, Users, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NetworkSwitcher() {
  const { user, checkAuth } = useAuth();
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinDialog, setJoinDialog] = useState(false);
  const [cisc, setCisc] = useState('');
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    fetchNetworks();
  }, []);

  const fetchNetworks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/networks/my`, { withCredentials: true });
      setNetworks(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const switchNetwork = async (orgId) => {
    setSwitching(true);
    try {
      await axios.post(`${API_URL}/api/networks/switch`, { organization_id: orgId }, { withCredentials: true });
      if (typeof checkAuth === 'function') await checkAuth();
      toast.success('Switched network');
      fetchNetworks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Switch failed'); }
    setSwitching(false);
  };

  const joinNetwork = async () => {
    if (!cisc.trim()) return toast.error('Enter CISC code');
    try {
      await axios.post(`${API_URL}/api/networks/join`, { cisc: cisc.trim() }, { withCredentials: true });
      toast.success('Joined network');
      setCisc('');
      setJoinDialog(false);
      fetchNetworks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const leaveNetwork = async (orgId) => {
    try {
      await axios.delete(`${API_URL}/api/networks/leave/${orgId}`, { withCredentials: true });
      toast.success('Left network');
      fetchNetworks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="network-switcher-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <ArrowRightLeft className="w-7 h-7 inline mr-3 text-[#00A6A0]" />My Networks
            </h1>
            <p className="text-muted-foreground mt-1">Manage your agency network memberships</p>
          </div>
          <Button className="btn-primary" onClick={() => setJoinDialog(true)} data-testid="join-network-btn">
            <Plus className="w-4 h-4 mr-2" />Join Network
          </Button>
        </div>

        {networks.length === 0 ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No network memberships found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {networks.map(net => {
              const isActive = net.id === user?.organization_id || net.id === user?.active_network_id;
              return (
                <Card key={net.id} className={`bg-card border ${isActive ? 'border-[#00A6A0]/40' : 'border-border'}`}
                  data-testid={`network-${net.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {net.logo_url ? (
                          <img src={net.logo_url} alt={net.name} className="w-12 h-12 rounded-xl object-cover border border-border" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-muted border border-border flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="text-foreground font-medium flex items-center gap-2">
                            {net.name}
                            {isActive && <CheckCircle2 className="w-4 h-4 text-[#00A6A0]" />}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span className="font-mono text-[#FF6F61]">{net.cisc}</span>
                            <span className="px-1.5 py-0.5 rounded-full bg-muted capitalize">{net.role?.replace('_', ' ')}</span>
                            {net.is_primary && <span className="px-1.5 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">Primary</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!isActive && (
                          <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419] text-xs"
                            onClick={() => switchNetwork(net.id)} disabled={switching}
                            data-testid={`switch-to-${net.id}`}>
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Switch
                          </Button>
                        )}
                        {!net.is_primary && (
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs"
                            onClick={() => leaveNetwork(net.id)} data-testid={`leave-${net.id}`}>
                            <LogOut className="w-3.5 h-3.5 mr-1" />Leave
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Join Dialog */}
        <Dialog open={joinDialog} onOpenChange={setJoinDialog}>
          <DialogContent className="glass-card border-border max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Join Network</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Enter the CISC (Company Identification Service Code) to join another agency network.</p>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">CISC Code</label>
                <Input value={cisc} onChange={e => setCisc(e.target.value)} placeholder="e.g., CISC-ABC12345"
                  className="bg-muted border-border text-foreground" data-testid="cisc-input" />
              </div>
              <Button className="w-full btn-primary" onClick={joinNetwork} data-testid="submit-join-btn">
                <Building2 className="w-4 h-4 mr-2" />Join Network
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
